/**
 * chat-director/playerBeat — turn the PLAYER's own chat actions into ground-truth
 * beats the director can thread + voice, so the world talks WITH the player (D14's
 * two-way contract) and the NPCs respond in-character to what the player just said.
 *
 * The player's *show-off* + *team-reaction* beats are canon owned by society/
 * ({@link import('@/society')}); this module covers the free-text / quick-reply
 * line, which is a chat-director concern (it never advances society canon — it just
 * seeds a conversation). Pure + deterministic: a function of (text, at).
 */
import type { Beat } from '@/shared';

/** Stable id for a player message beat (so identical re-sends dedup naturally). */
function messageId(text: string, at: number): string {
  return `player-msg-${at}-${hashText(text)}`;
}

function hashText(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * A beat representing the player saying something in chat. Marked `byPlayer` so the
 * voice/select layers know NPCs should *reply* to it (never speak it themselves),
 * and `normal` priority so the town reacts soon — but it never outranks a real
 * highlight event. Warm by construction: it carries only the player's own words.
 */
export function playerMessageBeat(
  text: string,
  at: number,
  channel: Beat['channel'] = 'zone',
): Beat {
  const trimmed = text.trim();
  return {
    id: messageId(trimmed, at),
    kind: 'chitchat',
    priority: 'normal',
    subjectTrainerId: 'player',
    involvedTrainerIds: ['player'],
    channel,
    data: {
      topic: 'player-says',
      byPlayer: true,
      said: trimmed,
      warm: true,
    },
    createdAt: at,
  };
}
